import classNames from 'classnames';
import DOMPurify from 'dompurify';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import {
  BtnBold,
  BtnBulletList,
  BtnClearFormatting,
  BtnItalic,
  BtnLink,
  BtnNumberedList,
  BtnRedo,
  BtnStrikeThrough,
  BtnStyles,
  BtnUnderline,
  BtnUndo,
  ContentEditableEvent,
  Editor,
  EditorProvider,
  Toolbar,
} from 'react-simple-wysiwyg';
import { isEditorHtml } from '~/utils/editorHtml';
import { FormFeedback } from './FormFeedback';
import { FormField } from './FormField';
import { getFieldError } from './formUtils';
import { FormFieldProps } from './interfaces';

interface WYSIWYGProps {
  onChange: (arg: unknown) => void;
  value: string;
  disabled?: boolean;
  flagged?: boolean;
  id: string;
  describedBy?: string;
  labelledBy?: string;
  invalid?: boolean;
  required?: boolean;
  /**
   * Called with the sanitized value whenever hydration (mount, or an
   * external value change) actually changed the value -- i.e. the raw
   * value handed to this component was unsafe/unescaped. The caller writes
   * this back into the form so an unrelated-field submit can't persist the
   * raw value (see RichTextArea below).
   */
  onHydrate?: (sanitized: string) => void;
}

const ALLOWED_TAGS = [
  'b',
  'i',
  'em',
  'strong',
  'a',
  'ul',
  'ol',
  'li',
  'p',
  'br',
  'u',
  's',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'pre',
];

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Prepares an incoming form value (API-stored HTML, or legacy plain text)
 * for hydration into the editor's `<Editor value=…>`, which injects it via
 * innerHTML. Without this, a stored value is rendered unsanitized on open,
 * and legacy plain text containing literal angle brackets -- including text
 * that happens to spell out a supported tag name, like "Use <pre> for
 * examples" -- is parsed as markup and loses the token on the next save.
 * `isEditorHtml` (shared with RichTextContent/stripHtml/html_to_text)
 * requires structurally complete markup, not just a bare opening tag.
 */
const sanitizeHydratedValue = (value: string): string => {
  if (!value) return value;
  if (!isEditorHtml(value)) {
    return escapeHtml(value);
  }
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });
};

const WYSIWYGEditor = (props: WYSIWYGProps) => {
  const { disabled, flagged, invalid } = props;
  const [value, setValue] = React.useState(() => sanitizeHydratedValue(props.value));
  // Tracks the last {raw, sanitized} pair we've already processed, so we can
  // tell a genuinely new external value apart from the echo of our own
  // onHydrate write-back landing as the next `props.value` -- re-running
  // sanitizeHydratedValue on an already-sanitized/escaped value would
  // double-encode it (e.g. "&lt;" -> "&amp;lt;").
  const syncRef = useRef({ raw: props.value, sanitized: value });
  const editorRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(props.onChange);
  onChangeRef.current = props.onChange;
  const onHydrateRef = useRef(props.onHydrate);
  onHydrateRef.current = props.onHydrate;

  // Mount: the initial sanitized value may differ from the raw form value
  // (an unsafe/unescaped stored value, or legacy plain text that needed
  // escaping) -- push it back into the form so submitting after editing an
  // unrelated field can't persist the raw value.
  useEffect(() => {
    if (syncRef.current.sanitized !== syncRef.current.raw) {
      onHydrateRef.current?.(syncRef.current.sanitized);
    }
  }, []);

  useEffect(() => {
    if (props.value === syncRef.current.raw) return; // already processed
    if (props.value === syncRef.current.sanitized) {
      // Echo of our own write-back -- adopt it as the processed baseline
      // without re-sanitizing.
      syncRef.current = { raw: props.value, sanitized: props.value };
      return;
    }
    const sanitized = sanitizeHydratedValue(props.value);
    setValue(sanitized);
    syncRef.current = { raw: props.value, sanitized };
    if (sanitized !== props.value) {
      onHydrateRef.current?.(sanitized);
    }
  }, [props.value]);

  // react-simple-wysiwyg memoizes its contenteditable DOM props without ARIA
  // attributes in the dependency list. Keep the real focusable node in sync
  // when validation changes instead of leaving a stale pristine state.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const syncAttribute = (name: string, value: string | undefined) => {
      if (value) editor.setAttribute(name, value);
      else editor.removeAttribute(name);
    };

    syncAttribute('aria-invalid', invalid ? 'true' : undefined);
    syncAttribute('aria-required', props.required ? 'true' : undefined);
    syncAttribute('aria-labelledby', props.labelledBy);
    syncAttribute('aria-describedby', props.describedBy);
    syncAttribute('aria-errormessage', invalid ? `${props.id}-error` : undefined);
  }, [invalid, props.describedBy, props.id, props.labelledBy, props.required]);

  const onChange = useCallback((e: ContentEditableEvent) => {
    let sanitized = DOMPurify.sanitize(e.target.value, {
      ALLOWED_TAGS,
      ALLOWED_ATTR: ['href', 'target', 'rel'],
    });
    if (sanitized === '<br>') {
      sanitized = '';
    }
    setValue(sanitized);
    syncRef.current = { raw: sanitized, sanitized };
    onChangeRef.current(sanitized);
  }, []);
  const [changeStyle, setChangeStyle] = useState(false);

  return (
    <React.Fragment>
      <div
        className={classNames(
          'editor rounded-md border overflow-hidden transition-colors duration-150',
          invalid
            ? 'border-error-50 ring-3 ring-error-50/8'
            : 'border-grey-800 focus-within:border-primary-50 focus-within:ring-3 focus-within:ring-primary-50/12'
        )}
      >
        <EditorProvider>
          <Editor
            ref={editorRef}
            id={props.id}
            value={value ? value : disabled ? 'No data entered' : ''}
            onChange={onChange}
            onBlur={() => {
              setChangeStyle(!!flagged);
            }}
            className={`w-full ${disabled ? 'bg-grey-900 h-72' : changeStyle ? 'bg-warning-50 h-64' : 'bg-white h-64'}`}
            placeholder="Enter text here..."
            disabled={disabled}
            aria-invalid={invalid || undefined}
            aria-required={props.required || undefined}
            aria-labelledby={props.labelledBy}
            aria-describedby={props.describedBy}
            aria-errormessage={invalid ? `${props.id}-error` : undefined}
            aria-multiline="true"
            role="textbox"
          >
            {!disabled && (
              <Toolbar>
                <BtnBold />
                <BtnItalic />
                <BtnBulletList />
                <BtnNumberedList />
                <BtnLink />
                <BtnUndo />
                <BtnRedo />
                <BtnClearFormatting />
                <BtnStrikeThrough />
                <BtnUnderline />
                <BtnStyles />
              </Toolbar>
            )}
          </Editor>
        </EditorProvider>
      </div>
    </React.Fragment>
  );
};

interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends FormFieldProps<TFieldValues, TName> {
  autoComplete?: string;
  rows?: number;
}
export const RichTextArea = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>
) => {
  const {
    name,
    description,
    cyTag,
    label,
    disabled,
    flagged,
    required,
    tooltip,
    className,
    errorCyTag,
    reserveFeedbackSpace,
  } = props;
  const {
    formState: { errors },
    setValue,
  } = useFormContext<TFieldValues>();
  const error = getFieldError(errors, name);
  return (
    <Controller
      name={name}
      render={({ field }) => {
        return (
          <FormField
            id={name}
            label={label}
            tooltip={tooltip}
            required={required}
            className={className}
            labelFor={false}
            feedback={
              <FormFeedback
                error={error?.message}
                errorId={`${name}-error`}
                errorCyTag={errorCyTag ?? (cyTag ? `${cyTag}-error` : undefined)}
                description={description}
                helperId={`${name}-helper`}
                reserveSpace={reserveFeedbackSpace}
              />
            }
          >
            <div data-cy={cyTag} className="text-left">
              <WYSIWYGEditor
                {...field}
                value={field.value || ''}
                disabled={disabled}
                flagged={flagged}
                id={name}
                labelledBy={label ? `${name}-label` : undefined}
                invalid={!!error}
                required={required}
                describedBy={
                  error?.message ? `${name}-error` : description ? `${name}-helper` : undefined
                }
                onHydrate={(sanitized) => {
                  // Sync only -- never dirty/touch the form for a value we
                  // sanitized on the caller's behalf, not the user's edit.
                  setValue(name, sanitized as unknown as TFieldValues[TName], {
                    shouldDirty: false,
                    shouldTouch: false,
                  });
                }}
              />
            </div>
          </FormField>
        );
      }}
    />
  );
};
